import type { AnchorHeight, IProjection } from './IProjection';

export type OverlayTarget = { kind: 'player'; id: string } | { kind: 'world'; x: number; y: number };

/** Wo ein Element in der Welt steht. */
export interface OverlayPlacement {
  x: number;
  y: number;
}

interface Anchor {
  el: HTMLElement;
  target: OverlayTarget;
  height: AnchorHeight;
}

export type Unsubscribe = () => void;

/** Restsichtbarkeit hinter einer Wand — verschwinden, aber nicht abrupt. */
const OCCLUDED_OPACITY = '0.12';

/**
 * Verankert DOM-Elemente (Video-Bubbles, Screenshare-Fenster, Hinweise)
 * an Welt-Positionen: pro Frame wird die Projektion direkt als transform
 * auf das Element geschrieben — compositor-freundlich, ohne React-Re-Render.
 *
 * Groesse nach Entfernung und Abblenden hinter Waenden kommen aus der
 * Projektion selbst — dieser Code weiss nicht, welche Ansicht laeuft, und
 * enthaelt keinen Zweig fuer die eine oder andere.
 */
export class OverlayProjector {
  private anchors = new Set<Anchor>();

  anchor(el: HTMLElement, target: OverlayTarget, height: AnchorHeight): Unsubscribe {
    const entry: Anchor = { el, target, height };
    this.anchors.add(entry);
    return () => this.anchors.delete(entry);
  }

  /** Alle Anker ausblenden — etwa waehrend die Ansicht gewechselt wird. */
  hideAll(): void {
    for (const anchor of this.anchors) anchor.el.style.visibility = 'hidden';
  }

  /** Pro Frame vom GameRuntime aufgerufen. */
  project(projection: IProjection, resolve: (target: OverlayTarget) => OverlayPlacement | null): void {
    for (const anchor of this.anchors) {
      const placement = resolve(anchor.target);
      // Position unbekannt oder ausserhalb des Sichtfelds -> ausblenden statt
      // falsch platzieren
      const screen = placement ? projection.worldToScreen(placement.x, placement.y, anchor.height) : null;
      if (!screen) {
        anchor.el.style.visibility = 'hidden';
        continue;
      }
      const style = anchor.el.style;
      style.visibility = 'visible';
      style.opacity = screen.occluded ? OCCLUDED_OPACITY : '';
      const scale = screen.scale === 1 ? '' : ` scale(${screen.scale.toFixed(3)})`;
      style.transform = `translate3d(${Math.round(screen.x)}px, ${Math.round(screen.y)}px, 0)${scale}`;
    }
  }
}
